import { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Images, Grid3x3 } from 'lucide-react';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import { PlotLibrary } from './PlotLibrary';
import { ImageLibrary } from './ImageLibrary';

interface LibrarySidebarProps {
  projectId: string;
}

export function LibrarySidebar({ projectId }: LibrarySidebarProps) {
  const { go } = useNav();
  const [activeTab, setActiveTab] = useState<'plots' | 'images'>('plots');

  // Reset to plots tab when project changes
  useEffect(() => {
    setActiveTab('plots');
  }, [projectId]);

  return (
    <div className="h-full bg-card border-r border-border flex flex-col" data-testid="library-sidebar">
      {/* Header */}
      <div className="h-12 px-4 border-b border-border flex items-center">
        <div className="flex gap-2 w-full">
          <Button
            variant="outline"
            size="sm"
            onClick={() => go(routes.project.gallery(projectId))}
            className="flex-1 h-9 text-xs"
            title="View Gallery"
          >
            <Images className="h-3 w-3 mr-1" />
            Gallery
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => go(routes.project.figures.list(projectId))}
            className="flex-1 h-9 text-xs"
            title="Multi-panel Figures"
          >
            <Grid3x3 className="h-3 w-3 mr-1" />
            Multi-panel
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="flex-1 flex flex-col overflow-hidden">
        <div className="px-2 pt-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="plots" className="text-xs">Plots</TabsTrigger>
            <TabsTrigger value="images" className="text-xs">
              Images
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <TabsContent value="plots" className="mt-0 h-full">
            <PlotLibrary projectId={projectId} />
          </TabsContent>
          <TabsContent value="images" className="mt-0 h-full">
            <ImageLibrary projectId={projectId} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

